
import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageCircle, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { pb, TutorProfile } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';
import { getProfilePictureUrl, getUserInitials } from '@/utils/profileUtils';
import { useNavigate } from 'react-router-dom';

interface SendMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  tutor: TutorProfile | null;
}

const SendMessageModal = ({ isOpen, onClose, tutor }: SendMessageModalProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  if (!tutor) return null;

  const getTutorName = () => {
    return tutor.expand?.user?.name || tutor.name || 'Unknown Tutor';
  };

  const handleSendMessage = async () => {
    if (!user) {
      // Store tutor info for after login redirect
      localStorage.setItem('messageReturnTutor', JSON.stringify({
        id: tutor.id,
        name: getTutorName()
      }));
      localStorage.setItem('authReturnUrl', 'message-modal');
      
      toast({
        title: "Sign In Required",
        description: "Please sign in to send a message to this tutor",
      });
      
      onClose();
      navigate('/auth');
      return;
    }

    if (!message.trim()) {
      toast({
        title: "Message Required",
        description: "Please enter a message",
        variant: "destructive"
      });
      return;
    }

    setIsSending(true);

    try {
      // First, find or create a conversation
      let conversation;
      try {
        const conversations = await pb.collection('conversations').getList(1, 1, {
          filter: `studentId = "${user.id}" && tutorId = "${tutor.user}"`,
        });
        
        if (conversations.items.length > 0) {
          conversation = conversations.items[0];
        }
      } catch (error) {
        console.log('No existing conversation found, will create new one');
      }

      if (!conversation) {
        // Create new conversation
        conversation = await pb.collection('conversations').create({
          studentId: user.id,
          tutorId: tutor.user,
          lastMessage: message.trim(),
          lastMessageAt: new Date().toISOString(),
        });
      }

      // Create the message
      const newMessage = await pb.collection('messages').create({
        conversationId: conversation.id,
        senderId: user.id,
        receiverId: tutor.user,
        message: message.trim(),
        isRead: false,
      });

      // Update conversation with last message
      await pb.collection('conversations').update(conversation.id, {
        lastMessage: message.trim(),
        lastMessageAt: new Date().toISOString(),
      });

      // Send notification to tutor about new message
      try {
        const { hybridNotificationService } = await import('@/services/hybridNotificationService');
        await hybridNotificationService.sendMessageNotification(
          tutor.user, 
          user.name || 'Student',
          'student'
        );
        console.log('New message notification sent to tutor:', tutor.user);
      } catch (notificationError) {
        console.error('Failed to send message notification:', notificationError);
        // Don't fail the whole operation if notification fails
      }

      toast({
        title: "Message Sent!",
        description: `Your message has been sent to ${getTutorName()}`,
      });

      setMessage('');
      onClose();
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send message",
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
    }
  };

  const profilePictureUrl = getProfilePictureUrl(tutor);
  console.log('Profile picture URL:', profilePictureUrl);
  console.log('Tutor data:', tutor);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-3">
            <MessageCircle className="h-5 w-5 text-blue-600" />
            <span>Send Message</span>
          </DialogTitle>
          <DialogDescription>
            Introduce yourself to the tutor, share your learning goals and ask any questions
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Tutor Info with Picture */}
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
            <Avatar className="h-16 w-16">
              {profilePictureUrl ? (
                <AvatarImage 
                  src={profilePictureUrl} 
                  alt={getTutorName()}
                  onError={(e) => {
                    console.log('Image failed to load:', profilePictureUrl);
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : null}
              <AvatarFallback className="bg-gradient-to-br from-blue-400 to-purple-500 text-white text-lg font-semibold">
                {getUserInitials(getTutorName())}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium text-gray-900 text-lg">{getTutorName()}</div>
              <div className="text-sm text-gray-500">
                {tutor.subjects?.slice(0, 2).join(', ')}
              </div>
              <div className="text-sm font-medium text-blue-600">
                ${tutor.hourlyRate || 0}/hr
              </div>
            </div>
          </div>

          {/* Message Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Your Message
            </label>
            <Textarea
              placeholder="Hi! I'm interested in learning [subject]. My learning goals are... I have questions about..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="resize-none"
            />
            <div className="text-xs text-gray-500 text-right">
              {message.length}/500
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendMessage}
              disabled={isSending || (user && !message.trim())}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSending ? (
                'Sending...'
              ) : !user ? (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Sign In to Send
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SendMessageModal;
